import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import requestGetEmailDeliveryStatus from '~/queries/customerCommunication/requestGetEmailDeliveryStatus';
import requestPatchEmailDeliveryStatus from '~/queries/customerCommunication/requestPatchEmailDeliveryStatus';

import type { paths } from '@luxuryescapes/contract-customer-communication';

type EmailDeliveryStatus =
  paths['/api/customer-communication/v2/subscriptions/users/{userId}/status']['get']['responses'][200]['content']['application/json']['result'];

type RemoveFromListType =
  paths['/api/customer-communication/v2/subscriptions/users/{userId}/status']['patch']['requestBody']['content']['application/json']['removeFromList'];

export type EmailDeliveryStatusKey = keyof EmailDeliveryStatus;
export type { EmailDeliveryStatus };

export const emailDeliveryStatusQueryKeys = {
  all: ['emailDeliveryStatus'] as const,
  detail: (userId: string, brand: string) => [...emailDeliveryStatusQueryKeys.all, userId, brand] as const,
};

export function useGetEmailDeliveryStatus(userId: string, brand: string, enabled = true) {
  return useQuery<EmailDeliveryStatus>({
    queryKey: emailDeliveryStatusQueryKeys.detail(userId, brand),
    queryFn: () => requestGetEmailDeliveryStatus(userId, brand),
    enabled: enabled && !!userId && !!brand,
  });
}

interface UpdateEmailDeliveryStatusParams {
  userId: string;
  brand: string;
  type: RemoveFromListType;
}

export function useUpdateEmailDeliveryStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, brand, type }: UpdateEmailDeliveryStatusParams) =>
      requestPatchEmailDeliveryStatus(userId, brand, type),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: emailDeliveryStatusQueryKeys.detail(variables.userId, variables.brand),
      });
    },
  });
}
